/**
 * How a task status reads on the Audit Insights rows: its label, its severity, and the
 * colours that severity is drawn in.
 *
 * All of it derives from the maps `shared/ui/ListCard` already owns, so this tab cannot
 * drift from the audit and task lists. Nothing here decides what a status *means* — that
 * decision stays in one place.
 */
import { BadgeType } from '@amzn/stencil-react-components/badge';
import { ChipBackgroundColor } from '@amzn/stencil-react-components/chip';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';

import { STATUS_BADGE_TYPE, STATUS_DISPLAY_LABEL } from 'src/v2/shared/ui/ListCard';

/** The shared maps as lookups, so reading one is not a dynamic property read. */
const LABELS = new Map(Object.entries(STATUS_DISPLAY_LABEL));
const BADGE_TYPES = new Map(Object.entries(STATUS_BADGE_TYPE));

/**
 * How one severity is drawn: the circle in front of a row, and the chip beside its name.
 *
 * One entry rather than three parallel maps, so a row cannot end up with a red chip beside
 * a blue circle. What a status *means* is still decided once, by `STATUS_BADGE_TYPE` — this
 * only translates that severity into Stencil's vocabularies.
 *
 * - `dotColor` is the `muted` tint, the step the prototype paints these circles:
 *   `fill.muted.red` is `#fde1df` against its `#fde8e8`. No ring; the colour is the whole
 *   signal and the words beside it say the rest.
 * - `chipColor` is a chip rather than a `Badge` because `Badge` paints `fill.vibrant.*` — a
 *   solid `#bb2929` for a failure — where the prototype's status pill is a light tint.
 * - `labelCss` darkens the label to the hue's own `-70` step, as the prototype does
 *   (`#c0392b` on its red pill). A chip otherwise leaves its text near-black, which reads
 *   as a tinted background with unrelated words on it. Held as the css object itself so
 *   emotion sees one stable value per severity rather than a new one each render.
 */
export interface StatusStyle {
  readonly dotColor: string;
  readonly chipColor: ChipBackgroundColor;
  readonly labelCss: { readonly color: string };
}

// Takes resolved values rather than token paths: `token` is type-checked against the
// literal path, and threading the path through a `string` parameter would lose that.
function style(
  dotColor: string,
  chipColor: ChipBackgroundColor,
  labelColor: string,
): StatusStyle {
  return { dotColor, chipColor, labelCss: { color: labelColor } };
}

const SEVERITY_STYLES = new Map<BadgeType, StatusStyle>([
  [
    BadgeType.Negative,
    style(
      token('color.fill.muted.red'),
      ChipBackgroundColor.Red,
      token('color.status.critical-icon'),
    ),
  ],
  [
    BadgeType.Warning,
    style(
      token('color.fill.muted.yellow'),
      ChipBackgroundColor.Yellow,
      token('color.status.warning-icon'),
    ),
  ],
  [
    BadgeType.Positive,
    style(
      token('color.fill.muted.green'),
      ChipBackgroundColor.Green,
      token('color.status.success-icon'),
    ),
  ],
  [
    BadgeType.Primary,
    style(
      token('color.fill.muted.blue'),
      ChipBackgroundColor.Blue,
      token('color.status.info-icon'),
    ),
  ],
  [
    BadgeType.Neutral,
    style(
      token('color.fill.muted.gray'),
      ChipBackgroundColor.Default,
      token('color.text.secondary'),
    ),
  ],
]);

/** Stands in for a severity this build does not recognise. */
const UNKNOWN_STYLE = style(
  token('color.fill.muted.gray'),
  ChipBackgroundColor.Default,
  token('color.text.secondary'),
);

/**
 * How a severity is drawn.
 *
 * Separate from the status lookup because Stencil owns `BadgeType`, not this tab: the two
 * severities `STATUS_BADGE_TYPE` never uses today are still reachable if it starts, and a
 * member Stencil adds later has to land somewhere.
 */
export function styleFor(badge: BadgeType): StatusStyle {
  return SEVERITY_STYLES.get(badge) ?? UNKNOWN_STYLE;
}

/**
 * The status as a reader should see it. Helios spells it `IN_PROGRESS`.
 *
 * An unmapped status falls through to the raw value, which is visible rather than blank,
 * so a status Helios adds shows up as something to map.
 */
export function taskStatusLabel(taskStatus: string): string {
  return LABELS.get(taskStatus) ?? taskStatus;
}

/** The badge a status is shown in, neutral for one this build does not recognise. */
export function taskStatusBadgeType(taskStatus: string): BadgeType {
  return BADGE_TYPES.get(taskStatus) ?? BadgeType.Neutral;
}

/**
 * How a task status is drawn on a row.
 *
 * Read through the badge rather than from the status directly, so the circle and the chip
 * beside it can never disagree about how serious a task is.
 */
export function taskStatusStyle(taskStatus: string): StatusStyle {
  return styleFor(taskStatusBadgeType(taskStatus));
}
